import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { AvReasonDialogComponent } from '../../../../Shared/Components/av-reason-dialog/av-reason-dialog.component';
import { AvEmptyStateComponent } from '../../../../Shared/Components/av-empty-state/av-empty-state.component';
import { AvStatusChipComponent } from '../../../../Shared/Components/av-status-chip/av-status-chip.component';
import { OpsFacade } from '../../../../../Facades/Ops/ops.facade';
import { DATE_TIME_FORMAT, VALUE_UNRESOLVED } from '../../../../../Consts/backoffice.const';
import {
  COMMAND_CADENCE,
  HEALTH_LABEL,
  HEALTH_TONE,
  RETRY_ALL_TITLE,
  RETRY_CONFIRM_LABEL,
  RETRY_CONSEQUENCE,
  RETRY_ONE_TITLE,
} from '../../../../../Consts/ops.const';
import { StatusTone } from '../../../../Shared/Types/status-tone.type';
import { ReasonDialogResult } from '../../../../Shared/Interfaces/reason-dialog.interface';

@Component({
  selector: 'app-ops-health',
  standalone: true,
  imports: [
    DatePipe,
    MatIconModule,
    MatTooltipModule,
    TranslateModule,
    AvEmptyStateComponent,
    AvStatusChipComponent,
  ],
  templateUrl: './ops-health.component.html',
  styleUrl: './ops-health.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'd-block' },
})
export class OpsHealthComponent {
  private readonly _dialog = inject(MatDialog);

  readonly facade = inject(OpsFacade);

  readonly dateTimeFormat = DATE_TIME_FORMAT;
  readonly unresolved = VALUE_UNRESOLVED;
  readonly cadences = COMMAND_CADENCE;

  readonly commandCount = computed<number>(() => this.facade.schedulerCommands().length);

  healthLabelKey(status: string): string {
    return HEALTH_LABEL[status] || status;
  }

  healthTone(status: string): StatusTone {
    return HEALTH_TONE[status] || 'neutral';
  }

  cadenceFor(command: string): string {
    return this.cadences[command.split(' ')[0] ?? command] || '';
  }

  reload(): void {
    this.facade.reload();
  }

  openRetry(uuid: string): void {
    const reference = this._dialog.open(AvReasonDialogComponent, {
      data: {
        title: RETRY_ONE_TITLE,
        consequence: RETRY_CONSEQUENCE,
        organizationName: this.facade.queueName(),
        subjectLabel: uuid,
        confirmLabel: RETRY_CONFIRM_LABEL,
        isDangerous: false,
      },
    });

    reference.afterClosed().subscribe((result?: ReasonDialogResult) => this._retry(uuid, result));
  }

  openRetryAll(): void {
    const reference = this._dialog.open(AvReasonDialogComponent, {
      data: {
        title: RETRY_ALL_TITLE,
        consequence: RETRY_CONSEQUENCE,
        organizationName: this.facade.queueName(),
        subjectLabel: `${this.facade.failedJobs().length} job(s)`,
        confirmLabel: RETRY_CONFIRM_LABEL,
        isDangerous: false,
      },
    });

    reference.afterClosed().subscribe((result?: ReasonDialogResult) => this._retryAll(result));
  }

  private _retry(uuid: string, result?: ReasonDialogResult): void {
    if (!result) {
      return;
    }

    this.facade.retry(uuid);
  }

  private _retryAll(result?: ReasonDialogResult): void {
    if (!result) {
      return;
    }

    this.facade.retryAll();
  }
}
